import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { reportsAPI } from '../../lib/api';
import { useProject } from '../../layout/ProjectLayout';
import { Avatar } from '../../components/ui';

const tooltipStyle = {
  background: 'var(--pm-raised)',
  border: '1px solid var(--pm-border)',
  borderRadius: 8,
  fontSize: 12,
  color: 'var(--pm-text)',
};

function Card({ title, children, wide }) {
  return (
    <section className={`rounded-lg border border-pm-border bg-pm-surface p-4 shadow-[var(--pm-elev-1)] ${wide ? 'md:col-span-2' : ''}`}>
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-pm-faint">{title}</h2>
      {children}
    </section>
  );
}

export default function ProjectReport() {
  const { project, roster } = useProject();
  const [report, setReport] = useState(null);

  useEffect(() => {
    reportsAPI.project(project.id).then(({ data }) => setReport(data));
  }, [project.id]);

  if (!report) return null;
  const pct = report.total_tasks ? Math.round((report.done_tasks / report.total_tasks) * 100) : 0;
  const memberRows = Object.entries(report.per_member_open)
    .map(([mid, n]) => ({ name: roster.find((m) => m.mid === mid)?.name || 'Unknown', n }))
    .sort((a, b) => b.n - a.n);

  return (
    <div className="mx-auto grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-2">
      <Card title="Completion">
        <div className="flex items-center gap-6">
          <div>
            <div className="pm-num text-3xl font-semibold">{pct}%</div>
            <div className="pm-num mt-1 text-xs text-pm-muted">
              {report.done_tasks} of {report.total_tasks} tasks done
            </div>
            <div className="pm-num mt-2 text-xs text-pm-muted">
              {(report.time_seconds / 3600).toFixed(1)} h tracked
            </div>
          </div>
          <div className="h-32 flex-1">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={report.status} dataKey="count" nameKey="group" innerRadius="55%" outerRadius="90%" strokeWidth={0}>
                  {report.status.map((s) => (
                    <Cell key={s.group} fill={s.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <ul className="mt-2 flex flex-wrap gap-3 text-xs text-pm-muted">
          {report.status.map((s) => (
            <li key={s.group} className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
              {s.group} {s.count}
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Completed — last 30 days">
        <div className="h-44">
          <ResponsiveContainer>
            <BarChart data={report.trend}>
              <XAxis dataKey="day" hide />
              <YAxis allowDecimals={false} width={22} tick={{ fill: 'var(--pm-faint)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: 'var(--pm-muted)' }} />
              <Bar dataKey="completed" fill={project.color} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="Milestones">
        {report.milestones.length === 0 && <p className="text-xs text-pm-faint">No milestones.</p>}
        <ul className="space-y-3">
          {report.milestones.map((m) => (
            <li key={m.id}>
              <div className="flex items-baseline justify-between text-sm">
                <span>{m.name}</span>
                <span className="pm-num text-xs text-pm-muted">
                  {m.done_tasks}/{m.total_tasks} · {m.progress}%
                </span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-pm-raised">
                <div className="h-full rounded-full" style={{ width: `${m.progress}%`, background: project.color }} />
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Open tasks per person">
        {memberRows.length === 0 && <p className="text-xs text-pm-faint">No open assigned tasks.</p>}
        <ul className="space-y-2">
          {memberRows.map((r) => (
            <li key={r.name} className="flex items-center gap-2 text-sm">
              <Avatar name={r.name} size={20} />
              <span className="flex-1">{r.name}</span>
              <span className="pm-num text-xs text-pm-muted">{r.n}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
